import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE, PAGE_META, BUSINESS, FAQ_SCHEMA_ITEMS } from "./pageMeta";

/**
 * <SEO /> — drop into any layout. Reads the current pathname and applies:
 *   - <title>
 *   - meta description / keywords
 *   - canonical
 *   - Open Graph + Twitter Card
 *   - JSON-LD structured data (LodgingBusiness on home, FAQPage on /faq)
 *
 * Per-page content lives in /components/seo/pageMeta.js — edit there.
 */
export default function SEO() {
  const { pathname } = useLocation();

  // Skip admin routes — they should never be indexed
  if (pathname.startsWith("/admin")) {
    return (
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Admin — Whale Cove Off Grid</title>
      </Helmet>
    );
  }

  const meta = PAGE_META[pathname] || PAGE_META["/"];
  const canonical = `${SITE.url}${pathname === "/" ? "" : pathname}`;
  const ogImage = meta.ogImage || SITE.defaultOgImage;

  // LodgingBusiness schema — emit only on home page
  const lodgingSchema =
    pathname === "/"
      ? {
          "@context": "https://schema.org",
          "@type": "LodgingBusiness",
          name: BUSINESS.name,
          description: BUSINESS.description,
          url: SITE.url,
          email: BUSINESS.email,
          image: SITE.defaultOgImage,
          address: {
            "@type": "PostalAddress",
            streetAddress: BUSINESS.streetAddress,
            addressLocality: BUSINESS.addressLocality,
            addressRegion: BUSINESS.addressRegion,
            addressCountry: BUSINESS.addressCountry,
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: BUSINESS.latitude,
            longitude: BUSINESS.longitude,
          },
          amenityFeature: BUSINESS.amenities.map((a) => ({
            "@type": "LocationFeatureSpecification",
            name: a,
          })),
          petsAllowed: true,
          smokingAllowed: false,
        }
      : null;

  // FAQPage schema — emit only on /faq
  const faqSchema =
    pathname === "/faq"
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_SCHEMA_ITEMS.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.a,
            },
          })),
        }
      : null;

  return (
    <Helmet>
      <html lang="en" />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      {meta.keywords && meta.keywords.length > 0 && (
        <meta name="keywords" content={meta.keywords.join(", ")} />
      )}
      <link rel="canonical" href={canonical} />
      <meta name="theme-color" content={SITE.themeColor} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={SITE.locale} />
      <meta property="og:type" content={meta.type || "website"} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured data */}
      {lodgingSchema && (
        <script type="application/ld+json">
          {JSON.stringify(lodgingSchema)}
        </script>
      )}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
}
